import React, { useState, useRef } from 'react';
import { Send, Paperclip, Image, Loader2, X } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface Message {
  id: string;
  senderId: string;
  senderName: string;
  senderAvatar: string;
  content: string;
  timestamp: string;
  attachments: Array<{
    id: string;
    type: 'image' | 'file';
    name: string;
    url: string;
    size: string;
  }>;
}

interface MessagingCenterProps {
  contractId: string;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif'];
const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
];

export function MessagingCenter({ contractId }: MessagingCenterProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState<Array<{
    id: string;
    type: 'image' | 'file';
    name: string;
    url: string;
    size: string;
  }>>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const handleSendMessage = async () => {
    if (!newMessage.trim() && pendingAttachments.length === 0) return;

    try {
      const message: Message = {
        id: crypto.randomUUID(),
        senderId: '1',
        senderName: 'You',
        senderAvatar: 'https://ui-avatars.com/api/?name=You',
        content: newMessage,
        timestamp: new Date().toISOString(),
        attachments: pendingAttachments,
      };

      setMessages([...messages, message]);
      setNewMessage('');
      setPendingAttachments([]);
      toast.success('Message sent successfully');
    } catch (error) {
      toast.error('Failed to send message');
      console.error('Failed to send message:', error);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'file') => {
    const files = e.target.files;
    if (!files?.length) return;

    setUploading(true);
    const newAttachments: typeof pendingAttachments = [];

    try {
      for (const file of Array.from(files)) {
        // Validate file size
        if (file.size > MAX_FILE_SIZE) {
          toast.error(`File ${file.name} is too large. Maximum size is 5MB`);
          continue;
        }

        // Validate file type
        if (type === 'image' && !ALLOWED_IMAGE_TYPES.includes(file.type)) {
          toast.error(`Invalid image type. Allowed types: JPEG, PNG, GIF`);
          continue;
        }

        if (type === 'file' && !ALLOWED_FILE_TYPES.includes(file.type)) {
          toast.error(`Invalid file type. Allowed types: PDF, DOC, DOCX, XLS, XLSX, TXT`);
          continue;
        }

        // Create object URL for preview
        const url = URL.createObjectURL(file);

        newAttachments.push({
          id: crypto.randomUUID(),
          type,
          name: file.name,
          url,
          size: `${(file.size / 1024).toFixed(1)} KB`,
        });
      }

      setPendingAttachments(prev => [...prev, ...newAttachments]);
      toast.success(`${newAttachments.length} file(s) attached successfully`);
    } catch (error) {
      toast.error('Failed to attach files');
      console.error('Failed to attach files:', error);
    } finally {
      setUploading(false);
      // Reset file inputs
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  const removeAttachment = (id: string) => {
    setPendingAttachments(prev => {
      const attachment = prev.find(a => a.id === id);
      if (attachment) {
        URL.revokeObjectURL(attachment.url);
      }
      return prev.filter(a => a.id !== id);
    });
  };

  return (
    <div className="flex h-[calc(100vh-16rem)] flex-col rounded-lg border border-gray-200 bg-white">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`mb-4 flex ${message.senderId === '1' ? 'justify-end' : 'justify-start'}`}
          >
            <div className="flex max-w-lg items-end space-x-2">
              {message.senderId !== '1' && (
                <img src={message.senderAvatar} alt="" className="h-8 w-8 rounded-full" />
              )}
              <div>
                <div
                  className={`rounded-lg px-4 py-2 ${
                    message.senderId === '1'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-900'
                  }`}
                >
                  {message.content && <p className="text-sm">{message.content}</p>}
                  {message.attachments.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {message.attachments.map((attachment) => (
                        <div
                          key={attachment.id}
                          className="flex items-center rounded bg-black/10 p-2 text-sm"
                        >
                          {attachment.type === 'image' ? (
                            <img
                              src={attachment.url}
                              alt={attachment.name}
                              className="max-h-32 rounded"
                            />
                          ) : (
                            <>
                              <Paperclip className="mr-2 h-4 w-4" />
                              <span>{attachment.name}</span>
                              <span className="ml-2 text-xs opacity-75">
                                ({attachment.size})
                              </span>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  {new Date(message.timestamp).toLocaleTimeString()}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pending Attachments */}
      {pendingAttachments.length > 0 && (
        <div className="border-t border-gray-200 p-2">
          <div className="flex flex-wrap gap-2">
            {pendingAttachments.map((attachment) => (
              <div
                key={attachment.id}
                className="flex items-center rounded-lg bg-gray-100 p-2 text-sm"
              >
                {attachment.type === 'image' ? (
                  <img
                    src={attachment.url}
                    alt={attachment.name}
                    className="mr-2 h-8 w-8 rounded object-cover"
                  />
                ) : (
                  <Paperclip className="mr-2 h-4 w-4 text-gray-500" />
                )}
                <span className="mr-2 max-w-[100px] truncate">{attachment.name}</span>
                <button
                  onClick={() => removeAttachment(attachment.id)}
                  className="rounded-full p-1 hover:bg-gray-200"
                >
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Message Input */}
      <div className="border-t border-gray-200 p-4">
        <div className="flex items-center space-x-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={(e) => handleFileSelect(e, 'file')}
            className="hidden"
            multiple
            accept={ALLOWED_FILE_TYPES.join(',')}
          />
          <input
            type="file"
            ref={imageInputRef}
            onChange={(e) => handleFileSelect(e, 'image')}
            className="hidden"
            multiple
            accept={ALLOWED_IMAGE_TYPES.join(',')}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 disabled:opacity-50"
          >
            <Paperclip className="h-5 w-5" />
          </button>
          <button
            onClick={() => imageInputRef.current?.click()}
            disabled={uploading}
            className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 disabled:opacity-50"
          >
            <Image className="h-5 w-5" />
          </button>
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 rounded-md border border-gray-300 px-4 py-2 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSendMessage();
              }
            }}
          />
          <button
            onClick={handleSendMessage}
            disabled={!newMessage.trim() && pendingAttachments.length === 0}
            className="rounded-full bg-indigo-600 p-2 text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {uploading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Send className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}